"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Container } from "@/components/ui/container";
import { Logo } from "@/components/brand/logo";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MobileNav } from "./mobile-nav";

// Phase 1 production nav: only live, production-ready surfaces. Academy is
// shown when its completeness flag is open (passed from the server layout).
// Other future features (AI Lab, Billing, Newsletter) remain gated. De-promoted
// pages (News, Guides, Best) stay indexed via the footer, not the primary nav.
export function SiteHeader({ academyPublic = false }: { academyPublic?: boolean }) {
  const [scrolled, setScrolled] = useState(false);

  const nav = [
    { href: "/firms", label: "Firms" },
    { href: "/tools", label: "Tools" },
    ...(academyPublic ? [{ href: "/academy", label: "Academy" }] : []),
  ];
  const fullNav = nav;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-2xl transition-[background,border-color] duration-200"
      style={{
        background: scrolled ? "rgba(10,11,13,0.85)" : "rgba(10,11,13,0.6)",
        borderBottom: `1px solid ${scrolled ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.02)"}`,
      }}
    >
      <Container className="flex h-[68px] items-center justify-between gap-4">
        <Link href="/" className="flex items-center shrink-0">
          <Logo size={52} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="px-3 py-2 rounded-md text-[13.5px] font-normal text-[var(--fg-muted)] hover:text-white hover:bg-white/[0.04] transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <Link href="/firms" className={cn(buttonVariants({ size: "sm" }), "hidden sm:inline-flex")}>
            Browse firms
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" />
              <path d="M13 5l7 7-7 7" />
            </svg>
          </Link>
          <MobileNav items={fullNav} />
        </div>
      </Container>
    </header>
  );
}
